import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShieldCheck, BookOpenCheck, Target, Palette, UserCog, ArrowRight } from "lucide-react";
import { LevelPicker } from "@/pages/Register";
import { StudyPlanForm } from "@/pages/StudyPlan";
import { NotificationSettings } from "@/components/NotificationSettings";
import { PhoneSettings } from "@/components/PhoneSettings";
import { usePrefs } from "@/context/PrefsContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function Segment({ label, value, options, onChange, testid }) {
  return (
    <div className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`${testid}-row`}>
      <span className="text-sm font-medium">{label}</span>
      <div className="inline-flex max-w-full flex-wrap rounded-full border border-border bg-card p-1 text-xs">
        {options.map(([v, l]) => <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)} className={`rounded-full px-3.5 py-1.5 transition-colors ${String(value) === String(v) ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>)}
      </div>
    </div>
  );
}

function TargetTab({ t, user, setUser }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => { api.get("/study-plan").then((r) => setPlan(r.data)).catch(() => {}); }, []);
  const setLevel = async (l) => { const { data } = await api.put("/profile/target", { target_level: l }); setUser(data); toast.success(t("saved")); api.get("/study-plan").then((r) => setPlan(r.data)).catch(() => {}); };
  return (
    <div className="space-y-5" data-testid="settings-tab-target">
      <div className="soft-card p-6" data-testid="target-level-settings">
        <p className="text-sm font-medium">{t("targetLevel")}</p>
        <div className="mt-3"><LevelPicker value={user?.target_level} onChange={setLevel} testid="settings-level" /></div>
      </div>
      <div className="soft-card p-6" data-testid="settings-study-plan">
        <p className="text-sm font-medium">Tanggal ujian JLPT & waktu belajar</p>
        <p className="mt-1 text-xs text-muted-foreground">Dipakai untuk menghitung target menit per hari di Beranda dan jadwal mingguan.</p>
        <div className="mt-4">{plan ? <StudyPlanForm plan={plan} onSaved={setPlan} compact /> : <p className="text-sm text-muted-foreground">{t("loading")}</p>}</div>
        <Link to="/study-plan" data-testid="settings-open-plan" className="mt-3 inline-flex min-h-[40px] items-center gap-1 text-xs font-medium underline-offset-4 hover:underline">{t("openPlan")}<ArrowRight className="h-3 w-3" /></Link>
      </div>
    </div>
  );
}

function DisplayTab({ t, prefs, save }) {
  return (
    <div className="soft-card divide-y divide-border px-6" data-testid="settings-tab-display">
      <Segment testid="theme" label={t("theme")} value={prefs.theme} onChange={(v) => save({ theme: v })} options={[["light", t("light")], ["dark", t("dark")], ["auto", t("auto")]]} />
      <Segment testid="furigana" label={t("furigana")} value={prefs.furigana} onChange={(v) => save({ furigana: v })} options={[["all", t("furiAll")], ["selective", t("furiSelective")], ["off", t("furiOff")]]} />
      <Segment testid="jpscale" label={t("jpSize")} value={prefs.jp_scale || 1} onChange={(v) => save({ jp_scale: Number(v) })} options={[[1, t("jpNormal")], [1.15, t("jpLarge")], [1.3, t("jpXL")]]} />
      <div className="py-4"><p lang="ja" className="jp text-lg" data-testid="jpscale-preview">日本語の文字は、このくらいの大きさで表示されます。</p></div>
      <Segment testid="bilingual" label={t("bilingual")} value={prefs.bilingual} onChange={(v) => save({ bilingual: v })} options={[["stacked", t("stacked")], ["tap", t("tap")]]} />
      <Segment testid="uilang" label={t("uiLang")} value={prefs.ui_lang} onChange={(v) => save({ ui_lang: v })} options={[["id", "Bahasa Indonesia"], ["jp", "日本語"]]} />
    </div>
  );
}

function AccountTab({ t, user, logout, nav }) {
  return (
    <div className="space-y-5" data-testid="settings-tab-account">
      <PhoneSettings />
      <NotificationSettings />
      <div className="grid gap-3 sm:grid-cols-2">
        <Link data-testid="settings-guide-link" to="/guide" className="soft-card flex items-center gap-3 p-5 transition-colors hover:bg-accent"><BookOpenCheck className="h-5 w-5 text-secondary" /><span><span className="block text-sm font-semibold">Panduan Sistem</span><span className="text-xs text-muted-foreground">Cara pakai & penjelasan fitur</span></span></Link>
        {user?.role === "superadmin" && <Link data-testid="settings-admin-link" to="/admin" className="soft-card flex items-center gap-3 border-primary/20 p-5 transition-colors hover:bg-accent"><ShieldCheck className="h-5 w-5 text-primary" /><span><span className="block text-sm font-semibold">Panel Super Admin</span><span className="text-xs text-muted-foreground">Lembaga & kontrak, soal, modul, maskot, notifikasi, reset</span></span></Link>}
      </div>
      <div className="soft-card p-6" data-testid="account-card">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("account")}</p>
        <p className="mt-2 text-sm font-medium">{user?.name} {user?.role === "superadmin" && <span data-testid="superadmin-badge" className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">Super Admin</span>}</p>
        <p className="text-sm text-muted-foreground">{user?.email}</p>
        {user?.institution_name && <p className="mt-1 text-xs text-muted-foreground">{t("institution")}: {user.institution_name} ({user.institution_code})</p>}
        <button data-testid="settings-logout-button" onClick={async () => { await logout(); nav("/"); }} className="mt-5 inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-accent"><LogOut className="h-4 w-4" />{t("logout")}</button>
      </div>
    </div>
  );
}

const TABS = [["target", "tabTarget", Target], ["display", "tabDisplay", Palette], ["account", "tabAccount", UserCog]];

export default function Settings() {
  const { prefs, update, t } = usePrefs();
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "target";
  const save = (p) => { update(p); toast.success(t("saved")); };

  return (
    <div className="mx-auto max-w-2xl fade-up" data-testid="settings-page">
      <h1 className="font-jpserif text-3xl sm:text-4xl">{t("settings")}</h1>
      <div className="mt-6 grid grid-cols-3 gap-1 rounded-2xl border border-border bg-card p-1" data-testid="settings-tabs">
        {TABS.map(([k, label, I]) => (
          <button key={k} data-testid={`settings-tab-${k}-button`} onClick={() => setParams({ tab: k })} className={`flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-1.5 py-2 text-center text-xs font-medium leading-tight transition-colors sm:px-2 sm:text-sm ${tab === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}><I className="hidden h-4 w-4 shrink-0 sm:block" /><span>{t(label)}</span></button>
        ))}
      </div>
      <div className="mt-5">
        {tab === "target" && <TargetTab t={t} user={user} setUser={setUser} />}
        {tab === "display" && <DisplayTab t={t} prefs={prefs} save={save} />}
        {tab === "account" && <AccountTab t={t} user={user} logout={logout} nav={nav} />}
      </div>
    </div>
  );
}
